import type {
  DynamicInvoiceMeasurementChartMeter,
  DynamicInvoiceMeasurementChartMeterUsage,
  DynamicInvoiceMeterUsage,
} from '@vy/api-definition';
import type { AggregationOperator } from '@vy/types';
import { AGGREGATION_OPERATORS, type LineItemProductVariant, type Period } from '@vy/types';
import type { VyDate } from '@vy/utils';
import {
  buildPeriod,
  entries,
  extract,
  getOptionalObjectProperty,
  getPeriodDays,
  isDateBetween,
  isEmptyArray,
  keys,
  purge,
  raise,
  raiseUnreachable,
  vyDate,
} from '@vy/utils';
import { OldVyPalette } from '../../../assets/old-themes/old-vy/palette';
import { barColors } from './dynamic-invoice-view-page-consts/dynamic-invoice-usage-line-item-bar-chart-options';
import {
  DYNAMIC_INVOICE_USAGE_LINE_ITEM_CHART_RESOLUTION,
  type DynamicInvoiceUsageLineItemChartResolution,
} from './dynamic-invoice-view-page-types/dynamic-invoice-usage-line-item-chart-resolution';

export function generateDatasets(
  metersData: DynamicInvoiceMeasurementChartMeter,
  perUnitLineItemProductVariants: LineItemProductVariant[] | undefined,
  billingCycle: Period,
  dateResolution: DynamicInvoiceUsageLineItemChartResolution,
  aggregationOperator: AggregationOperator,
  meterNamesMap?: Record<string, string>,
  treatLastAsDaily = false,
) {
  return entries(metersData).map(([meterId, meterUsageData], index) => {
    const { totalUnitValues, totalPriceValues } = getChartValues(
      meterUsageData,
      billingCycle,
      dateResolution,
      aggregationOperator,
      treatLastAsDaily,
    );

    return {
      label: getMeterLabel(meterId, perUnitLineItemProductVariants, meterNamesMap),
      data: totalUnitValues,
      backgroundColor: barColors[index] ?? barColors[0],
      metadata: {
        priceValues: totalPriceValues,
      },
    };
  });
}

function getMeterLabel(
  meterId: string,
  perUnitLineItemProductVariants: LineItemProductVariant[] | undefined,
  meterNamesMap?: Record<string, string>,
) {
  // Prefer meterNamesMap if provided
  if (meterNamesMap && meterNamesMap[meterId]) {
    return meterNamesMap[meterId];
  }

  // Fallback to perUnitLineItemProductVariants for backward compatibility
  if (perUnitLineItemProductVariants) {
    return perUnitLineItemProductVariants.find((item) => item.meter?.meterId === meterId)?.meter?.name;
  }

  // Final fallback to meterId
  return meterId;
}

export function buildPerUnitChartData(
  chartLabels: string[],
  metersData: DynamicInvoiceMeasurementChartMeter,
  perUnitLineItemProductVariants: LineItemProductVariant[] | undefined,
  billingCycle: Period,
  dateResolution: DynamicInvoiceUsageLineItemChartResolution,
  aggregationOperator: AggregationOperator,
  meterNamesMap?: Record<string, string>,
  treatLastAsDaily = false,
) {
  return {
    labels: chartLabels,
    datasets: generateDatasets(
      metersData,
      perUnitLineItemProductVariants,
      billingCycle,
      dateResolution,
      aggregationOperator,
      meterNamesMap,
      treatLastAsDaily,
    ),
  };
}

export function buildTiersChartData(
  chartLabels: string[],
  chartData: {
    totalUnitValues: number[];
    totalPriceValues: number[];
  },
  datasetLabel?: string,
) {
  return {
    labels: chartLabels,
    datasets: [
      {
        label: datasetLabel,
        data: chartData.totalUnitValues,
        backgroundColor: OldVyPalette.primary.main,
        metadata: {
          priceValues: chartData.totalPriceValues,
        },
      },
    ],
  };
}

export function getChartLabels(
  billingCycle: Period,
  dateResolution: DynamicInvoiceUsageLineItemChartResolution,
): string[] {
  const dates = getPeriodDays(billingCycle);

  switch (dateResolution) {
    case DYNAMIC_INVOICE_USAGE_LINE_ITEM_CHART_RESOLUTION.DAY:
      return getDailyLabels(dates);
    case DYNAMIC_INVOICE_USAGE_LINE_ITEM_CHART_RESOLUTION.WEEK:
      return getWeeklyLabels(dates, billingCycle);
    case DYNAMIC_INVOICE_USAGE_LINE_ITEM_CHART_RESOLUTION.MONTH:
      return getMonthlyLabels(dates);
    default:
      return raiseUnreachable(dateResolution);
  }
}

function getDailyLabels(dates: VyDate[]) {
  return dates.map((date) => date.startOf('day').format('DD/MM'));
}

function getWeeklyLabels(dates: VyDate[], periodLimits: Period) {
  return dates
    .filter((_, index) => index % 7 === 0)
    .map((date, weekIndex) => {
      const startOfWeek = weekIndex === 0 ? vyDate(periodLimits.startTime) : date.startOf('day');
      const endOfCurrentIteratee = date.add(6, 'day');
      const endOfWeek = endOfCurrentIteratee.isBefore(vyDate(periodLimits.endTime))
        ? endOfCurrentIteratee
        : vyDate(periodLimits.endTime);

      const startOfWeekLabel = startOfWeek.format('DD/MM');
      const endOfWeekLabel = endOfWeek.format('DD/MM');

      return `${startOfWeekLabel} - ${endOfWeekLabel}`;
    });
}

function getMonthlyLabels(dates: VyDate[]) {
  return dates
    .filter((date, index, arr) => index === 0 || date.month() !== arr[index - 1].month())
    .map((date) => date.startOf('month').format('MM/YYYY'));
}

export function getChartValues(
  chartData: DynamicInvoiceMeasurementChartMeterUsage,
  selectedPeriod: Period,
  dateResolution: DynamicInvoiceUsageLineItemChartResolution,
  aggregationOperator: AggregationOperator,
  treatLastAsDaily = false,
) {
  const fullBillingCycleMeterUsage = getSelectedPeriodMeterUsage(chartData, selectedPeriod);
  const sortedMapValues = getSortedMapValues(fullBillingCycleMeterUsage);

  switch (dateResolution) {
    case DYNAMIC_INVOICE_USAGE_LINE_ITEM_CHART_RESOLUTION.DAY:
      return aggregateChartDataByDay(sortedMapValues);
    case DYNAMIC_INVOICE_USAGE_LINE_ITEM_CHART_RESOLUTION.WEEK:
      return aggregateChartDataByWeek(sortedMapValues, aggregationOperator, treatLastAsDaily);
    case DYNAMIC_INVOICE_USAGE_LINE_ITEM_CHART_RESOLUTION.MONTH:
      return aggregateChartDataByMonth(sortedMapValues, selectedPeriod, aggregationOperator, treatLastAsDaily);

    default:
      return raiseUnreachable(dateResolution);
  }
}

function aggregateChartDataByDay(data: DynamicInvoiceMeterUsage[]) {
  return data.reduce<{ totalUnitValues: number[]; totalPriceValues: number[] }>(
    (acc, entry) => {
      acc.totalUnitValues.push(entry.totalUnits);
      acc.totalPriceValues.push(entry.totalPrice);

      return acc;
    },
    { totalUnitValues: [], totalPriceValues: [] },
  );
}

function aggregateChartDataByWeek(
  data: DynamicInvoiceMeterUsage[],
  aggregationOperator: AggregationOperator,
  treatLastAsDaily: boolean,
) {
  const amountOfWeeks = Math.ceil(data.length / 7);
  const weeks = Array.from({ length: amountOfWeeks }, (_, weekIndex) => {
    const weekData = data.slice(weekIndex * 7, (weekIndex + 1) * 7);

    return aggregateData(weekData, aggregationOperator, treatLastAsDaily);
  });

  const totalUnitValues = extract(weeks, 'totalUnitValues');
  const totalPriceValues = extract(weeks, 'totalPriceValues');

  return {
    totalUnitValues,
    totalPriceValues,
  };
}

function aggregateChartDataByMonth(
  data: DynamicInvoiceMeterUsage[],
  billingCycle: Period,
  aggregationOperator: AggregationOperator,
  treatLastAsDaily: boolean,
) {
  const startDate = vyDate(billingCycle.startTime).startOf('day');
  const endDate = vyDate(billingCycle.endTime).endOf('day');

  const startYear = startDate.year();
  const endYear = endDate.year();
  const startMonth = startDate.month();
  const endMonth = endDate.month();
  const monthsCount = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;

  const months = Array.from({ length: monthsCount }, (_, monthOffset) => {
    const currentMonthStart = startDate.add(monthOffset, 'month').startOf('month');
    const currentMonthEndUnclamped = currentMonthStart.endOf('month');
    const currentMonthStartClamped = currentMonthStart.isBefore(startDate) ? startDate : currentMonthStart;
    const currentMonthEnd = currentMonthEndUnclamped.isAfter(endDate) ? endDate : currentMonthEndUnclamped;

    const monthData = data.filter((_, index) => {
      const currentDate = startDate.add(index, 'day');

      return isDateBetween(currentDate.toDate(), currentMonthStartClamped.toDate(), currentMonthEnd.toDate());
    });

    return aggregateData(monthData, aggregationOperator, treatLastAsDaily);
  });

  const totalUnitValues = extract(months, 'totalUnitValues');
  const totalPriceValues = extract(months, 'totalPriceValues');

  return {
    totalUnitValues,
    totalPriceValues,
  };
}

function aggregateData(
  data: DynamicInvoiceMeterUsage[],
  aggregationOperator: AggregationOperator,
  treatLastAsDaily: boolean,
) {
  if (isEmptyArray(data)) {
    return { totalUnitValues: 0, totalPriceValues: 0 };
  }

  const sumUnits = data.reduce((sum, { totalUnits }) => sum + totalUnits, 0);
  const sumPrice = data.reduce((sum, { totalPrice }) => sum + totalPrice, 0);

  return aggregateByOperator(aggregationOperator, sumUnits, sumPrice, data.length, treatLastAsDaily);
}

function aggregateByOperator(
  aggregationOperator: AggregationOperator,
  sumUnits: number,
  sumPrice: number,
  length: number,
  treatLastAsDaily: boolean,
) {
  switch (aggregationOperator) {
    case AGGREGATION_OPERATORS.Average:
      return { totalUnitValues: sumUnits / length, totalPriceValues: sumPrice / length };
    case AGGREGATION_OPERATORS.Sum:
    case AGGREGATION_OPERATORS.Count:
      return { totalUnitValues: sumUnits, totalPriceValues: sumPrice };
    case AGGREGATION_OPERATORS.Last:
      return treatLastAsDaily
        ? { totalUnitValues: sumUnits, totalPriceValues: sumPrice }
        : raise('Temporal aggregation operators are not supported for per unit line item charts');
    case AGGREGATION_OPERATORS.Distinct:
      return { totalUnitValues: 0, totalPriceValues: 0 };
    case AGGREGATION_OPERATORS.Max:
    case AGGREGATION_OPERATORS.Min:
    case AGGREGATION_OPERATORS.First:
      return raise('Temporal aggregation operators are not supported for per unit line item charts');
    default:
      return raiseUnreachable(aggregationOperator);
  }
}

function getSortedMapValues(chartData: DynamicInvoiceMeasurementChartMeterUsage) {
  const sortedMapKeys = getSortedKeysAsNumbers(chartData);

  return purge(sortedMapKeys.map((key) => getOptionalObjectProperty(chartData, key.toString())));
}

function getSortedKeysAsNumbers(chartData: DynamicInvoiceMeasurementChartMeterUsage) {
  const mapKeys = keys(chartData);
  const mapKeysAsNumbers = mapKeys.map((key) => Number(key));

  return mapKeysAsNumbers.sort((a, b) => a - b);
}

function getSelectedPeriodMeterUsage(
  meterUsage: DynamicInvoiceMeasurementChartMeterUsage,
  selectedPeriod: Period,
) {
  const periodDays = getPeriodDays(selectedPeriod);
  const copiedMeterUsage = { ...meterUsage };

  periodDays.forEach((periodDay) => {
    const isMeterUsageDaysContainsPeriodDay = getIsMeterUsageDaysContainsPeriodDay(meterUsage, periodDay);

    const dateKey = periodDay.valueOf();
    if (!isMeterUsageDaysContainsPeriodDay) {
      copiedMeterUsage[dateKey] = {
        totalUnits: 0,
        totalPrice: 0,
      };
    }
  });

  return copiedMeterUsage;
}

function getIsMeterUsageDaysContainsPeriodDay(
  meterUsage: DynamicInvoiceMeasurementChartMeterUsage,
  periodDay: VyDate,
) {
  return Object.keys(meterUsage).some((stringifiedDateInMilliseconds) => {
    const dateInMilliseconds = parseInt(stringifiedDateInMilliseconds);
    const meterUsageDay = vyDate(dateInMilliseconds);
    const meterUsageDayPeriod = getDayPeriodFromDay(meterUsageDay);

    return isDateBetween(periodDay.toDate(), meterUsageDayPeriod);
  });
}

function getDayPeriodFromDay(day: VyDate) {
  const startOfDay = day.startOf('day');
  const endOfDay = day.endOf('day');

  return buildPeriod(startOfDay.toDate(), endOfDay.toDate());
}
